"use client";

/**
 * Global Error Boundary — catches React rendering errors in the App Router.
 *
 */
import { captureError } from "@/lib/sentry";
import { useEffect, useState } from "react";

/**
 * Derive locale from the URL pathname client-side.
 * Falls back to "en" if locale cannot be determined.
 */
function useLocaleFromUrl(): string {
  const [locale, setLocale] = useState("en");
  useEffect(() => {
    if (typeof window !== "undefined") {
      const segments = window.location.pathname.split("/").filter(Boolean);
      const supportedLocales = ["en", "fr", "es", "zh", "ar"];
      const detected = segments[0];
      if (detected && supportedLocales.includes(detected)) {
        setLocale(detected);
      }
    }
  }, []);
  return locale;
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const locale = useLocaleFromUrl();
  const dir = locale === "ar" ? "rtl" : "ltr";

  useEffect(() => {
    captureError(error, { source: "global-error" });
  }, [error]);

  return (
    <html lang={locale} dir={dir}>
      <body>
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <h2>Something went wrong!</h2>
          <p style={{ color: "#666", marginTop: "1rem" }}>
            An unexpected error occurred. Please try again.
          </p>
          <button
            onClick={reset}
            style={{
              marginTop: "1rem",
              padding: "0.5rem 1rem",
              borderRadius: "0.375rem",
              backgroundColor: "#2563eb",
              color: "white",
              border: "none",
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        </div>
      </body>
    </html>
  );
}
